import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import { COMPANY_NAME, PHONE, SITE_NAME, SITE_URL } from '../constants/site';

const UPDATED = 'May 25, 2026';

export default function CancellationRefundPage() {
  return (
    <PolicyLayout title="Cancellation & Refund Policy" lastUpdated={UPDATED}>
      <PolicySection title="1. Introduction">
        <p>
          This policy describes how cancellations and refunds work when you book through {SITE_NAME} ({SITE_URL}),
          operated by {COMPANY_NAME}. Because we act as an intermediary, refund eligibility is primarily determined by
          the travel supplier&apos;s fare rules and terms.
        </p>
      </PolicySection>

      <PolicySection title="2. Before you cancel">
        <PolicyList
          items={[
            'Review fare rules shown at checkout and in your confirmation email.',
            'Note whether your ticket or rate is refundable, partially refundable, or non-refundable.',
            'Understand that service fees charged by Farenodex may be non-refundable even when travel is refunded.',
            'Cancel as early as possible — closer to departure often means higher penalties or no refund.',
          ]}
        />
      </PolicySection>

      <PolicySection title="3. Flight cancellations and refunds">
        <PolicySubsection title="3.1 Voluntary cancellation">
          <p>
            If you choose to cancel, the airline applies penalties per your fare type. Refundable fares may return the
            full fare minus fees; restricted fares may forfeit the ticket value or issue a travel credit with an
            expiration date.
          </p>
        </PolicySubsection>
        <PolicySubsection title="3.2 Involuntary cancellation">
          <p>
            When an airline cancels or significantly delays your flight, you may be entitled to rebooking or a refund
            under airline policy and applicable regulations. Contact us promptly so we can help coordinate with the
            carrier.
          </p>
        </PolicySubsection>
        <PolicySubsection title="3.3 24-hour risk-free period (where applicable)">
          <p>
            For qualifying bookings made seven or more days before departure, U.S. DOT rules may entitle you to cancel
            within twenty-four (24) hours of purchase for a full refund when booked directly through a U.S. carrier or
            when the fare rules provide such a right. Eligibility depends on supplier and booking channel.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="4. Hotels, cars, packages, and cruises">
        <p>
          Each product displays its own cancellation window (e.g., free cancellation until 6:00 PM local time two days
          before arrival). Prepaid non-refundable hotel rates, car prepaid rates, and cruise deposits often cannot be
          refunded after the stated deadline. No-show policies may charge the full stay or rental period.
        </p>
      </PolicySection>

      <PolicySection title="5. Refund method and timing">
        <PolicyList
          items={[
            'Approved refunds are returned to the original form of payment unless otherwise agreed.',
            'Processing typically takes 7–21 business days; some airlines or banks take longer.',
            'Travel credits issued by suppliers expire per their terms — use before the deadline.',
            'Chargebacks may delay or reverse refunds; contact us before disputing with your bank when possible.',
          ]}
        />
      </PolicySection>

      <PolicySection title="6. Partial refunds and credits">
        <p>
          When only part of a trip is used or canceled (e.g., one leg of a round trip), suppliers may calculate refunds
          using complex fare construction. You may receive less than a proportional refund. Our agents can provide
          estimates before you confirm a cancellation request.
        </p>
      </PolicySection>

      <PolicySection title="7. Insurance and third-party protection">
        <p>
          If you purchased trip protection or insurance through a third party, claims are handled by that provider under
          its policy. {SITE_NAME} does not control insurance adjudication timelines or coverage decisions.
        </p>
      </PolicySection>

      <PolicySection title="8. How to request a cancellation or refund">
        <p>
          Call <a href={`tel:${PHONE.replace(/[^\d+]/g, '')}`}>{PHONE}</a> (24/7), visit{' '}
          <Link to="/flight/support">Contact Us</Link>, or use <Link to="/flight/bookings">Manage Booking</Link>. Have your
          confirmation number, passenger names, and travel dates ready.
        </p>
      </PolicySection>

      <PolicySection title="9. Related policies">
        <p>
          <Link to="/flight/post-ticketing-fees">Post Ticketing Fees</Link> · <Link to="/flight/service-fees">Service Fees</Link> ·{' '}
          <Link to="/flight/fulfillment-policy">Fulfillment Policy</Link>
        </p>
      </PolicySection>

      <PolicySection title="10. Contact us">
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
